import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { generatePath, To } from "react-router";
import { graphql, useFragment } from "react-relay";
import { CheckCircleIcon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import { formatEntitySubmissionCodePath } from "@/utils/routing";
import { useIsPast } from "@/utils/hooks";
import ViewSubmissionsModal, {
  VersionSubmissionLink,
} from "./ViewSubmissionsModal";
import WorkspaceVersionPublishButton from "./WorkspaceVersionPublishButton";
import { WorkspaceVersionCardActionsFragment$key } from "./__generated__/WorkspaceVersionCardActionsFragment.graphql";

const Fragment = graphql`
  fragment WorkspaceVersionCardActionsFragment on WorkspaceVersion {
    id
    version
    publishedAt
    viewerCanPublish: viewerCan(action: PUBLISH_WORKSPACE_VERSION)
    workspace {
      id
      slug
      viewerCanCreateVersion: viewerCan(action: CREATE_WORKSPACE_VERSION)
    }
    fromCompetition @ifAllowed {
      id
      title
      slug
      currentStage {
        noCode
        submissionDeadline
      }
      events(first: 1) {
        nodes {
          event {
            slug
          }
        }
      }
    }
    # Submissions (visible to the viewer) linking this version, for the
    # SUBMISSION section.
    submissions(first: 50) {
      nodes {
        id
        entity {
          username
        }
        stage {
          seq
          events(first: 1) {
            nodes {
              event {
                slug
              }
              competition {
                slug
                title
              }
            }
          }
        }
      }
    }
    ...WorkspaceVersionPublishButtonFragment
  }
`;

export interface WorkspaceVersionCardActionsProps {
  version: WorkspaceVersionCardActionsFragment$key;
}

function SubmissionHeading() {
  return (
    <>
      <div className="w-full border-t border-grey-200" />
      <p className="text-xs tracking-wide uppercase text-grey-600">
        <FormattedMessage defaultMessage="Submission" />
      </p>
    </>
  );
}

/// The footer of the version aside card: the publish button for drafts, and
/// the track-submission section (submit / view submissions) for published
/// versions, falling back to the "Active" badge when there is nothing to show.
export default function WorkspaceVersionCardActions({
  version: versionFragment,
}: WorkspaceVersionCardActionsProps) {
  const version = useFragment(Fragment, versionFragment);
  const [viewSubmissionsOpen, setViewSubmissionsOpen] = useState(false);
  const isPublished = version.publishedAt !== null;
  // A published version cloned from a track can be submitted to that track:
  // the button links to the track's submission page with this version
  // preloaded (the user reviews and submits there — no auto-create).
  const fromCompetition = version.fromCompetition;
  const fromCompetitionEventSlug =
    fromCompetition?.events.nodes[0]?.event.slug ?? null;
  const submissionsClosed = useIsPast(
    fromCompetition?.currentStage.submissionDeadline,
  );
  const submitToTrackTo: To | null =
    version.workspace.viewerCanCreateVersion &&
    isPublished &&
    fromCompetition &&
    fromCompetition.currentStage.noCode &&
    !submissionsClosed &&
    fromCompetitionEventSlug
      ? {
          pathname: generatePath("/challenges/:owner/tracks/:slug/submission", {
            owner: fromCompetitionEventSlug,
            slug: fromCompetition.slug,
          }),
          search: `?link=${encodeURIComponent(version.id)}&linkLabel=${encodeURIComponent(`${version.workspace.slug} v${version.version}`)}`,
        }
      : null;
  // Submissions (visible to the viewer) that link this version, each resolved
  // to its browse page. When any exist, the card offers "View submission(s)"
  // instead of "Submit to track".
  const versionSubmissions: VersionSubmissionLink[] = (
    version.submissions?.nodes ?? []
  ).flatMap((submission) => {
    const eventCompetition = submission.stage.events.nodes[0];
    if (!eventCompetition) return [];
    return [
      {
        id: submission.id,
        title: eventCompetition.competition.title,
        to: formatEntitySubmissionCodePath({
          entity: submission.entity.username,
          owner: eventCompetition.event.slug,
          slug: eventCompetition.competition.slug,
          version: undefined,
          fileKind: "PACKAGE",
          stage: submission.stage.seq,
        }),
      },
    ];
  });

  if (!isPublished) {
    return (
      <>
        <WorkspaceVersionPublishButton version={version} className="w-full">
          <FormattedMessage defaultMessage="Publish version" />
        </WorkspaceVersionPublishButton>
        {version.viewerCanPublish && fromCompetition && (
          <p className="text-xs text-grey-600">
            <FormattedMessage defaultMessage="Publishing creates an immutable snapshot. Only published versions can be submitted to the track." />
          </p>
        )}
      </>
    );
  }

  if (versionSubmissions.length > 0) {
    return (
      <>
        <SubmissionHeading />
        <Badge variant="success" hasPrefix shape="pill" className="gap-1">
          <CheckCircleIcon className="w-3 h-3" />
          <FormattedMessage defaultMessage="Submitted to track" />
        </Badge>
        {versionSubmissions.length === 1 ? (
          <Link
            to={versionSubmissions[0].to}
            variant="secondary"
            className="w-full"
          >
            <FormattedMessage defaultMessage="View submission" />
          </Link>
        ) : (
          <>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => setViewSubmissionsOpen(true)}
            >
              <FormattedMessage defaultMessage="View submissions" />
            </Button>
            <ViewSubmissionsModal
              open={viewSubmissionsOpen}
              onOpenChange={setViewSubmissionsOpen}
              submissions={versionSubmissions}
            />
          </>
        )}
      </>
    );
  }

  if (submitToTrackTo && fromCompetition) {
    return (
      <>
        <SubmissionHeading />
        <Link to={submitToTrackTo} variant="primary" className="w-full">
          <FormattedMessage defaultMessage="Submit to track" />
        </Link>
        <p className="text-xs text-grey-600">
          <FormattedMessage
            defaultMessage="Submits v{version} to {track} for jury review. You can submit more versions until the deadline; your best score counts."
            values={{
              version: version.version,
              track: fromCompetition.title,
            }}
          />
        </p>
      </>
    );
  }

  return (
    <Button
      className="w-full disabled:border-success-300 disabled:text-success-700 disabled:bg-success-50"
      variant="secondary"
      hue="success"
      disabled
    >
      <CheckCircleIcon className="w-5 h-5" />
      <FormattedMessage defaultMessage="Active" />
    </Button>
  );
}
